(() => {
  'use strict';

  const root = document.documentElement;
  const controllers = new WeakMap();

  const syncHeaderVars = (header) => {
    if (!(header instanceof HTMLElement)) return;
    const shell = header.closest('[data-zyma-shell]');
    const headerRect = header.getBoundingClientRect();
    const headerHeight = Math.ceil(headerRect.height);
    // Announcement bar + header only: measuring to the header's bottom edge ignores dropdown panels.
    const groupHeight = shell instanceof HTMLElement
      ? Math.ceil(headerRect.bottom - shell.getBoundingClientRect().top)
      : headerHeight;
    document.body?.style.setProperty('--header-height', `${headerHeight}px`);
    document.body?.style.setProperty('--header-group-height', `${groupHeight}px`);
  };

  const syncCartCount = (count) => {
    if (!Number.isFinite(Number(count))) return;
    const value = Math.max(0, Number(count));
    document.querySelectorAll('[data-zyma-cart-count]').forEach((node) => {
      node.textContent = value > 99 ? '99+' : String(value);
    });
    document.querySelectorAll('.zyma-bag-action').forEach((node) => {
      node.setAttribute('aria-label', `Bag, ${value} ${value === 1 ? 'item' : 'items'}`);
    });
  };

  const canonicalizeNavigationCopy = (scope = document) => {
    const base = scope instanceof Element ? scope : document;
    base.querySelectorAll?.('a[href="/collections/make-your-bundle"]').forEach((anchor) => {
      if (anchor.children.length === 0 && anchor.textContent.trim() === 'Make Your Bundle') {
        anchor.textContent = 'Build Your Bundle';
      }
    });
  };

  const initHeader = (header) => {
    if (!(header instanceof HTMLElement) || controllers.has(header)) return;
    const controller = new AbortController();
    const { signal } = controller;
    controllers.set(header, controller);

    canonicalizeNavigationCopy(header.closest('[data-zyma-shell]') || header);

    const shell = header.closest('[data-zyma-shell]');
    const section = header.closest('.shopify-section');
    const dialog = section?.querySelector('[data-zyma-mobile-dialog]') || document.querySelector('[data-zyma-mobile-dialog]');
    const openButton = header.querySelector('[data-zyma-open-mobile]');
    const closeButton = dialog?.querySelector('[data-zyma-close-dialog]');
    const navDetails = [...header.querySelectorAll('[data-zyma-nav-details]')];
    let restoreFocus = null;

    if (openButton instanceof HTMLButtonElement) openButton.setAttribute('aria-expanded', 'false');

    const closeDialog = ({ restore = true } = {}) => {
      if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
      root.classList.remove('zyma-dialog-open');
      if (openButton instanceof HTMLButtonElement) openButton.setAttribute('aria-expanded', 'false');
      if (restore && restoreFocus instanceof HTMLElement && document.contains(restoreFocus)) {
        requestAnimationFrame(() => restoreFocus.focus({ preventScroll: true }));
      }
    };

    const openDialog = () => {
      if (!(dialog instanceof HTMLDialogElement)) return;
      // Always return to the menu button: Safari does not focus buttons on click, so activeElement is often <body>.
      restoreFocus = openButton;
      if (!dialog.open) dialog.showModal();
      root.classList.add('zyma-dialog-open');
      if (openButton instanceof HTMLButtonElement) openButton.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => {
        const target = closeButton || dialog.querySelector('a,button,summary,input,select,textarea,[tabindex]:not([tabindex="-1"])');
        if (target instanceof HTMLElement) target.focus({ preventScroll: true });
      });
    };

    openButton?.addEventListener('click', openDialog, { signal });
    closeButton?.addEventListener('click', () => closeDialog(), { signal });

    if (dialog instanceof HTMLDialogElement) {
      canonicalizeNavigationCopy(dialog);
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeDialog();
      }, { signal });
      dialog.addEventListener('close', () => {
        root.classList.remove('zyma-dialog-open');
        // Start collapsed next time; wait for the slide-out so the drawer doesn't jump while leaving.
        setTimeout(() => {
          if (!dialog.open) dialog.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));
        }, 320);
        if (openButton instanceof HTMLButtonElement) openButton.setAttribute('aria-expanded', 'false');
        if (restoreFocus instanceof HTMLElement && document.contains(restoreFocus)) {
          requestAnimationFrame(() => restoreFocus.focus({ preventScroll: true }));
        }
      }, { signal });
    }

    // Dropdowns open on hover with a mouse (like the Shop menu) and on click / tap / keyboard everywhere.
    const hoverCapable = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
    navDetails.forEach((details) => {
      const summary = details.querySelector(':scope > summary');
      let hoverTimer = 0;
      let openedByHover = false;
      signal.addEventListener('abort', () => clearTimeout(hoverTimer));

      details.addEventListener('toggle', () => {
        if (!details.open) {
          openedByHover = false;
          return;
        }
        navDetails.forEach((other) => {
          if (other !== details) other.removeAttribute('open');
        });
        header.querySelectorAll('[data-zyma-shop-nav].is-open').forEach((nav) => nav.dispatchEvent(new CustomEvent('zyma:shop-close')));
      }, { signal });
      details.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse' || !hoverCapable.matches) return;
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(() => {
          if (details.open) return;
          openedByHover = true;
          details.open = true;
        }, 60);
      }, { signal });
      details.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse' || !hoverCapable.matches) return;
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(() => {
          // Keep it open while a keyboard user is inside the list.
          if (details.contains(document.activeElement) && document.activeElement !== summary) return;
          details.removeAttribute('open');
        }, 180);
      }, { signal });
      summary?.addEventListener('click', (event) => {
        if (!openedByHover) return;
        // Already open from hovering: a click should keep it open rather than toggle it shut.
        event.preventDefault();
        openedByHover = false;
      }, { signal });
      details.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !details.open) return;
        event.preventDefault();
        details.removeAttribute('open');
        const summary = details.querySelector(':scope > summary');
        if (summary instanceof HTMLElement) summary.focus({ preventScroll: true });
      }, { signal });
    });

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Node)) return;
      if (shell instanceof HTMLElement && shell.contains(event.target)) return;
      navDetails.forEach((details) => details.removeAttribute('open'));
    }, { signal });

    const desktop = window.matchMedia('(min-width: 990px)');
    const onBreakpoint = (event) => {
      if (event.matches && dialog instanceof HTMLDialogElement && dialog.open) closeDialog({ restore: false });
    };
    if (typeof desktop.addEventListener === 'function') desktop.addEventListener('change', onBreakpoint, { signal });
    else desktop.addListener?.(onBreakpoint);

    let resizeFrame = 0;
    const queueHeaderSync = () => {
      if (resizeFrame) return;
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = 0;
        syncHeaderVars(header);
      });
    };
    syncHeaderVars(header);
    window.addEventListener('resize', queueHeaderSync, { passive: true, signal });
    // Fonts and the logo can change the header height after first paint.
    window.addEventListener('load', () => syncHeaderVars(header), { once: true, signal });
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(() => syncHeaderVars(header));
      observer.observe(header);
      if (shell instanceof HTMLElement) observer.observe(shell);
      signal.addEventListener('abort', () => observer.disconnect());
    }

    // "Float over homepage hero": the pill stays transparent until the page scrolls.
    if (shell instanceof HTMLElement && shell.dataset.overlay === 'true') {
      // Only writes the attribute when the state flips, so no frame throttling is needed.
      const syncScrolled = () => {
        const scrolled = window.scrollY > 8 ? 'true' : 'false';
        if (shell.dataset.scrolled !== scrolled) shell.dataset.scrolled = scrolled;
      };
      window.addEventListener('scroll', syncScrolled, { passive: true, signal });
      syncScrolled();
    }

    signal.addEventListener('abort', () => cancelAnimationFrame(resizeFrame));
  };

  const destroyAll = (scope) => {
    if (!(scope instanceof Element)) return;
    const headers = scope.matches('[data-zyma-header]') ? [scope] : [...scope.querySelectorAll('[data-zyma-header]')];
    headers.forEach((header) => {
      controllers.get(header)?.abort();
      controllers.delete(header);
    });
  };

  const initAll = (scope = document) => {
    canonicalizeNavigationCopy(scope);
    if (scope instanceof Element && scope.matches('[data-zyma-header]')) initHeader(scope);
    scope.querySelectorAll?.('[data-zyma-header]').forEach(initHeader);
  };

  const start = () => initAll();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));

  // Rewards links open the Smile.io panel through its #smile-home deep link. Modal dialogs (cart drawer,
  // mobile menu) sit in the top layer and would cover the panel, so close them first.
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-zyma-rewards-link]') : null;
    const hash = link?.getAttribute('href') || '';
    if (!hash.startsWith('#')) return;
    event.preventDefault();
    document.querySelectorAll('dialog[open]').forEach((dialog) => dialog.close());
    // Clear a stale identical hash first so setting it again still fires hashchange.
    if (window.location.hash === hash) history.replaceState(null, '', window.location.pathname + window.location.search);
    window.location.hash = hash.slice(1);
  });
  document.addEventListener('cart:update', (event) => {
    const count = event.detail?.data?.itemCount ?? event.detail?.resource?.item_count;
    syncCartCount(count);
  });
})();
